import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Terminal, ShieldCheck, Cloud, Brain, Zap } from "lucide-react";
import { SITE } from "@/lib/site";
import { ThemeBar, PreviewFooter } from "@/pages/preview/MidnightPreview";

/* ───────────────────── CYBER SECOPS THEME PREVIEW ─────────────────────── */

const BG = "#000000";
const PANEL = "#0A0A0A";
const TEXT = "#E5E5E5";
const MUTED = "#65D973";
const GREEN = "#00FF7F";
const AMBER = "#FFB627";
const RED = "#FF5454";
const MONO = "'JetBrains Mono', 'Space Mono', monospace";
const SANS = "'IBM Plex Sans', sans-serif";

const SCANLINE_CSS = `
  .scanlines::before {
    content: ""; position: absolute; inset: 0; pointer-events: none;
    background: repeating-linear-gradient(0deg, rgba(0,255,127,0.04) 0px, rgba(0,255,127,0.04) 1px, transparent 1px, transparent 3px);
    mix-blend-mode: screen;
  }
  @keyframes blink { 50% { opacity: 0; } }
  .cursor::after { content: "▊"; margin-left: 4px; animation: blink 1s steps(1) infinite; color: #00FF7F; }
  .glow-green { text-shadow: 0 0 12px rgba(0,255,127,0.35); }
`;

export default function CyberPreview() {
  const [boot, setBoot] = useState(0);
  useEffect(() => {
    const lines = ["mounting /usr/aksahni", "loading agent: ajay-sahni.exe", "running: trainer.boot()", "ready"];
    let i = 0;
    const id = setInterval(() => {
      i++;
      setBoot(i);
      if (i >= lines.length) clearInterval(id);
    }, 350);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen relative" style={{ background: BG, color: TEXT, fontFamily: SANS }} data-testid="preview-cyber">
      <style>{SCANLINE_CSS}</style>
      <div className="scanlines absolute inset-0 pointer-events-none" />

      <ThemeBar variant="cyber" />

      {/* Top status bar — terminal look */}
      <div className="border-b" style={{ borderColor: "rgba(0,255,127,0.18)", background: BG }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-12 flex items-center justify-between" style={{ fontFamily: MONO, fontSize: "11px" }}>
          <div className="flex items-center gap-4">
            <span style={{ color: GREEN }}>● ONLINE</span>
            <span className="opacity-70">[ aksahni.com / v2026.1 ]</span>
            <span className="opacity-50 hidden md:inline">user: visitor@external · uptime 99.9%</span>
          </div>
          <div className="flex items-center gap-3 opacity-80" style={{ color: AMBER }}>
            <span className="hidden sm:inline">THREAT_LVL: LOW</span>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: AMBER }} />
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="border-b" style={{ borderColor: "rgba(0,255,127,0.18)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" style={{ fontFamily: MONO }}>
            <Terminal size={16} style={{ color: GREEN }} />
            <span style={{ color: GREEN }} className="glow-green">ajay@aksahni:~$</span>
            <span className="hidden sm:inline" style={{ color: TEXT }}>./run.sh</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm" style={{ fontFamily: MONO }}>
            {["expertise","programs","voices","insights","contact"].map((l) => (
              <a key={l} className="opacity-70 hover:opacity-100 transition-opacity" style={{ color: TEXT }}>
                <span style={{ color: GREEN }}>./</span>{l}
              </a>
            ))}
          </nav>
          <button className="px-4 py-2 text-xs uppercase tracking-wider" style={{ background: GREEN, color: "#000", fontFamily: MONO, fontWeight: 700 }}>
            ./book --call
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs mb-6" style={{ fontFamily: MONO, color: GREEN }}>
              {Array.from({ length: boot }).map((_, i) => (
                <div key={i} className="opacity-70">
                  [{(i * 0.35).toFixed(2)}s] {["mounting /usr/aksahni","loading agent: ajay-sahni.exe","running: trainer.boot()","ready"][i]}
                </div>
              ))}
              {boot >= 4 && <div className="mt-1"><span style={{ color: GREEN }}>$</span> trainer --status</div>}
            </div>
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="font-bold text-4xl sm:text-5xl md:text-6xl leading-[1.02]"
              style={{ fontFamily: MONO, color: TEXT, letterSpacing: "-0.02em" }}
            >
              <span className="block">&gt; ENGINEERING_TRUST<span style={{ color: GREEN }}>.</span></span>
              <span className="block mt-1 cursor glow-green" style={{ color: GREEN }}>AT_THE_SPEED_OF_CLOUD</span>
            </motion.h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed opacity-80" style={{ fontFamily: SANS, color: TEXT }}>
              <span style={{ color: GREEN }}>//</span> 20+ yrs IT &nbsp;<span style={{ color: AMBER }}>·</span>&nbsp; 15+ yrs training &nbsp;<span style={{ color: AMBER }}>·</span>&nbsp; CISSP / CCSP / CEH &nbsp;<span style={{ color: AMBER }}>·</span>&nbsp; Microsoft Sentinel · CrowdStrike · Defender XDR · Splunk &nbsp;<span style={{ color: AMBER }}>·</span>&nbsp; GenAI Defence
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm uppercase tracking-wider" style={{ background: GREEN, color: "#000", fontFamily: MONO, fontWeight: 700 }}>
                $ book --discovery <ArrowUpRight size={15} />
              </button>
              <button className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm uppercase tracking-wider border" style={{ borderColor: GREEN, color: GREEN, fontFamily: MONO, fontWeight: 600 }}>
                $ ls ./programs
              </button>
            </div>

            <div className="mt-10 flex flex-wrap gap-2" style={{ fontFamily: MONO }}>
              {[{ l: "[CISSP]", c: GREEN }, { l: "[AZ-500]", c: AMBER }, { l: "[SENTINEL]", c: GREEN }, { l: "[GENAI-RT]", c: AMBER }].map((p) => (
                <span key={p.l} className="text-[11px] px-2.5 py-1 border" style={{ borderColor: p.c, color: p.c }}>
                  {p.l}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            {/* Bracket-frame portrait */}
            <div className="relative aspect-[4/5] max-w-md mx-auto" style={{ fontFamily: MONO }}>
              <div className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2" style={{ borderColor: GREEN }} />
              <div className="absolute -top-3 -right-3 w-10 h-10 border-t-2 border-r-2" style={{ borderColor: GREEN }} />
              <div className="absolute -bottom-3 -left-3 w-10 h-10 border-b-2 border-l-2" style={{ borderColor: GREEN }} />
              <div className="absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2" style={{ borderColor: GREEN }} />
              <div className="relative h-full w-full overflow-hidden" style={{ border: `1px solid ${GREEN}` }}>
                <img src={SITE.portrait} alt="Ajay Sahni" className="h-full w-full object-cover object-top" style={{ filter: "grayscale(1) contrast(1.15) brightness(0.95)" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.0) 50%, rgba(0,0,0,0.6) 100%), linear-gradient(90deg, rgba(0,255,127,0.10) 0%, transparent 30%, transparent 70%, rgba(0,255,127,0.10) 100%)" }} />
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-[10px]" style={{ color: GREEN }}>
                  <span>FEED_01 · LIVE</span>
                  <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: GREEN }} />
                </div>
                <div className="absolute bottom-4 left-4 right-4" style={{ color: TEXT }}>
                  <div className="text-[10px]" style={{ color: GREEN }}>// asset.id = ajay-sahni</div>
                  <div className="text-lg font-bold mt-0.5" style={{ fontFamily: MONO, color: TEXT }}>AJAY_SAHNI</div>
                  <div className="text-[11px] opacity-80" style={{ color: MUTED }}>{SITE.tagline}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats — log entries */}
      <section className="border-y py-12" style={{ borderColor: "rgba(0,255,127,0.18)", background: PANEL }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6" style={{ fontFamily: MONO }}>
          {[
            { k: "years.IT", v: "20+" },
            { k: "years.training", v: "15+" },
            { k: "pros.trained", v: "10,000+" },
            { k: "enterprise.clients", v: "50+" },
          ].map((s) => (
            <div key={s.k}>
              <div className="text-[10px] uppercase tracking-wider opacity-60" style={{ color: GREEN }}>const {s.k} =</div>
              <div className="mt-1 text-3xl md:text-4xl font-bold glow-green" style={{ color: GREEN }}>{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Detections / Expertise */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: GREEN, fontFamily: MONO }}>// 01 — expertise</div>
          <h2 className="text-3xl md:text-5xl tracking-tight" style={{ fontFamily: MONO, color: TEXT, letterSpacing: "-0.01em" }}>
            <span style={{ color: GREEN }}>$</span> grep -r "skills" ./ajay
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Cloud, t: "cloud_arch", b: "Azure · AWS · GCP · landing zones", c: GREEN },
              { icon: ShieldCheck, t: "cybersec", b: "CISSP · CCSP · CEH · Security+", c: AMBER },
              { icon: Brain, t: "ai_security", b: "GenAI defence · LLM red-team", c: GREEN },
              { icon: Zap, t: "secops", b: "Sentinel · Splunk · CrowdStrike", c: AMBER },
              { icon: Terminal, t: "ms_stack", b: "Entra · Intune · Defender · M365", c: GREEN },
              { icon: ShieldCheck, t: "ai_governance", b: "AAISM · AIGP · SecAI+ · CAISR", c: AMBER },
            ].map((c) => (
              <div key={c.t} className="p-5 border" style={{ borderColor: "rgba(0,255,127,0.20)", background: PANEL }}>
                <div className="flex items-center gap-3" style={{ fontFamily: MONO }}>
                  <c.icon size={16} style={{ color: c.c }} />
                  <span style={{ color: c.c }} className="text-sm">{c.t}</span>
                  <span className="ml-auto text-[10px] opacity-60" style={{ color: c.c }}>[OK]</span>
                </div>
                <div className="mt-3 text-sm opacity-80" style={{ color: TEXT }}>{c.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 border p-10 md:p-14" style={{ borderColor: GREEN, background: PANEL, fontFamily: MONO }}>
          <div className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: GREEN }}># exec</div>
          <h2 className="text-3xl md:text-4xl glow-green" style={{ color: GREEN, fontFamily: MONO, letterSpacing: "-0.01em" }}>
            ./engage.sh --intent=corporate-training
          </h2>
          <p className="mt-5 max-w-xl text-sm opacity-80" style={{ color: TEXT, fontFamily: SANS }}>
            Drop your team's specs — duration, audience, outcomes — and receive
            a tailored program proposal within 24 hours.
          </p>
          <button className="mt-7 inline-flex items-center gap-2 px-7 py-3 text-xs uppercase tracking-wider" style={{ background: GREEN, color: "#000", fontWeight: 700 }}>
            $ initiate.session <ArrowUpRight size={14} />
          </button>
        </div>
      </section>

      <PreviewFooter variant="cyber" />
    </div>
  );
}
